import { useState } from "react";

import dayjs from "dayjs";
import {
  Check,
  Package,
  Pencil,
  Printer,
  Trash2,
  Warehouse,
  X,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getProductionUnitLabel } from "~/constants/units";
import type { BatchPallet } from "~/types/wms";

interface PalletTableProps {
  pallets: BatchPallet[];
  onPrintSticker: (pallet: BatchPallet) => void;
  onUpdateBoxCount?: (palletId: string, boxCount: number) => void;
  onDeletePallet?: (palletId: string) => void;
  showDeleteButton?: boolean;
  productionUnit?: string;
}

const STATUS_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; className: string }
> = {
  in_production: {
    label: "En Producción",
    icon: <Package className="h-3 w-3" />,
    className: "bg-blue-100 text-blue-700 border-blue-200",
  },
  ready_for_ingestion: {
    label: "Listo",
    icon: <Check className="h-3 w-3" />,
    className: "bg-green-100 text-green-700 border-green-200",
  },
  in_warehouse: {
    label: "En Bodega",
    icon: <Warehouse className="h-3 w-3" />,
    className: "bg-purple-100 text-purple-700 border-purple-200",
  },
};

export function PalletTable({
  pallets,
  onPrintSticker,
  onUpdateBoxCount,
  onDeletePallet,
  showDeleteButton,
  productionUnit,
}: PalletTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);

  if (pallets.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <Package className="mx-auto mb-3 h-12 w-12 opacity-50" />
        <p>No hay pallets producidos todavía.</p>
        <p className="text-sm">Haz clic en "Iniciar Pallet" para comenzar.</p>
      </div>
    );
  }

  const startEdit = (pallet: BatchPallet) => {
    setEditingId(pallet.id);
    setEditValue(pallet.initial_box_count);
  };

  const confirmEdit = () => {
    if (editingId && onUpdateBoxCount && editValue > 0) {
      onUpdateBoxCount(editingId, editValue);
    }
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const canModify = (pallet: BatchPallet) =>
    pallet.status === "ready_for_ingestion" ||
    pallet.status === "in_production";

  const canDelete = (pallet: BatchPallet) =>
    pallet.status === "in_production" ||
    pallet.status === "ready_for_ingestion";

  // Calculate summary
  const completedPallets = pallets.filter((p) => p.production_ended_at);
  const totalBoxes = completedPallets.reduce(
    (sum, p) => sum + p.initial_box_count,
    0
  );

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Operador</TableHead>
            <TableHead>Inicio</TableHead>
            <TableHead>Fin</TableHead>
            <TableHead className="text-right">
              {productionUnit === "UNI" ? "Unidades" : "Cajas"}
            </TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="w-40"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...pallets]
            .sort((a, b) => b.sequence_number - a.sequence_number)
            .map((pallet, index) => {
              const statusConfig = STATUS_CONFIG[pallet.status] || {
                label: pallet.status,
                icon: null,
                className: "bg-gray-100 text-gray-700",
              };
              const isEditing = editingId === pallet.id;

              return (
                <TableRow
                  key={pallet.id}
                  className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                >
                  <TableCell className="font-medium">
                    {pallet.sequence_number}
                  </TableCell>
                  <TableCell>{pallet.producer_name}</TableCell>
                  <TableCell>
                    {pallet.production_started_at
                      ? dayjs(pallet.production_started_at).format("HH:mm")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {pallet.production_ended_at
                      ? dayjs(pallet.production_ended_at).format("HH:mm")
                      : "-"}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <Input
                          type="number"
                          min="1"
                          className="h-8 w-20 text-right"
                          value={editValue}
                          onChange={(e) =>
                            setEditValue(parseInt(e.target.value) || 0)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") confirmEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          onFocus={(e) => e.target.select()}
                          autoFocus
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={confirmEdit}
                        >
                          <Check className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={cancelEdit}
                        >
                          <X className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </div>
                    ) : (
                      pallet.initial_box_count || "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={statusConfig.className}>
                      {statusConfig.icon}
                      <span className="ml-1">{statusConfig.label}</span>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {pallet.status !== "in_production" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => onPrintSticker(pallet)}
                        >
                          <Printer className="mr-1 h-4 w-4" />
                          Imprimir
                        </Button>
                      )}
                      {pallet.sticker_printed_at && (
                        <span className="text-green-600">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                      {canModify(pallet) &&
                        onUpdateBoxCount &&
                        pallet.status !== "in_production" &&
                        !isEditing && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => startEdit(pallet)}
                          >
                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        )}
                      {showDeleteButton &&
                        canDelete(pallet) &&
                        onDeletePallet && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            onClick={() => onDeletePallet(pallet.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>

      {/* Summary */}
      <div className="border-t pt-4 text-sm text-muted-foreground">
        <span>
          <strong>{pallets.length}</strong> pallets |{" "}
          <strong>{totalBoxes}</strong>{" "}
          {getProductionUnitLabel(productionUnit, totalBoxes)}
        </span>
      </div>
    </div>
  );
}
